import React, { useState, useEffect } from 'react';
import { X, User, Search, Loader2, UserPlus, Check } from 'lucide-react';
import { getAvailableChefs, assignChef } from '@/services/filieresService';
import type { AvailableChef } from '@/types/filiere.types';

interface AssignChefModalProps {
  filiereId: string;
  filiereName: string;
  assignedChefIds: string[];
  onClose: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

const AssignChefModal: React.FC<AssignChefModalProps> = ({
  filiereId,
  filiereName,
  assignedChefIds,
  onClose,
  onSuccess,
  onError,
}) => {
  const [availableChefs, setAvailableChefs] = useState<AvailableChef[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedChefId, setSelectedChefId] = useState<string | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);

  // Load available chefs
  useEffect(() => {
    const loadChefs = async () => {
      try {
        const chefs = await getAvailableChefs(filiereId);
        setAvailableChefs(chefs);
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Erreur lors du chargement');
      } finally {
        setIsLoading(false);
      }
    };
    loadChefs();
  }, [filiereId, onError]);

  // Filter chefs by search
  const filteredChefs = availableChefs.filter((chef) => {
    const query = searchQuery.toLowerCase();
    const fullName = `${chef.firstName} ${chef.lastName}`.toLowerCase();
    return (
      fullName.includes(query) ||
      (chef.email?.toLowerCase().includes(query) ?? false)
    );
  });

  // Check if chef is already assigned
  const isAssigned = (chefId: string) => assignedChefIds.includes(chefId);

  // Handle assignment
  const handleAssign = async () => {
    if (!selectedChefId) return;

    setIsAssigning(true);
    try {
      await assignChef(filiereId, selectedChefId);
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Erreur lors de l'assignation");
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <UserPlus className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Assigner un Chef de Filière
                </h2>
                <p className="text-sm text-gray-500 truncate max-w-[200px]">{filiereName}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Chefs List */}
          <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg mb-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
              </div>
            ) : filteredChefs.length === 0 ? (
              <div className="text-center py-8">
                <User className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {searchQuery
                    ? 'Aucun chef trouvé pour cette recherche'
                    : 'Aucun chef de filière disponible'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredChefs.map((chef) => {
                  const assigned = isAssigned(chef.id);
                  const selected = selectedChefId === chef.id;

                  return (
                    <button
                      key={chef.id}
                      onClick={() => !assigned && setSelectedChefId(chef.id)}
                      disabled={assigned}
                      className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                        assigned
                          ? 'bg-gray-50 opacity-60 cursor-not-allowed'
                          : selected
                          ? 'bg-primary-50 border-l-4 border-primary-500'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          assigned
                            ? 'bg-green-100'
                            : selected
                            ? 'bg-primary-100'
                            : 'bg-gray-100'
                        }`}
                      >
                        {assigned ? (
                          <Check className="w-5 h-5 text-green-600" />
                        ) : (
                          <User
                            className={`w-5 h-5 ${
                              selected ? 'text-primary-600' : 'text-gray-400'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`font-medium truncate ${
                            assigned ? 'text-gray-500' : 'text-gray-900'
                          }`}
                        >
                          {chef.firstName} {chef.lastName}
                        </div>
                        <div className="text-sm text-gray-500 truncate">{chef.email ?? '-'}</div>
                      </div>
                      {assigned && (
                        <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          Assigné
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isAssigning}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Annuler
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedChefId || isAssigning}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Assignation...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Assigner
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignChefModal;
